'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Link, Typography} from '../../primitives';
import {CardPendingOverlay} from './CardPendingOverlay';
import {ListingCardBadges} from './ListingCardBadges';
import {ListingCardMedia} from './ListingCardMedia';
import {ListingCardMeta, ListingCardPrimaryMeta} from './ListingCardMeta';
import type {ListingCardProps} from './types';

const CardRoot = styled(Box)(({theme}) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.radii.card,
  overflow: 'hidden',
  background: theme.palette.colors.surface.neutral.default,
  textDecoration: 'none',
  color: 'inherit',
  '@media (hover: hover)': {
    '&:hover [data-card-actions]': {opacity: 1},
    '&:hover [data-card-interactive]': {pointerEvents: 'auto'},
  },
}));

const CardLink = styled(Link)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  borderRadius: 'inherit',
  color: 'inherit',
  textDecoration: 'none',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.colors.border.focus.default}`,
    outlineOffset: 2,
  },
}));

const Body = styled(Box)(({theme}) => ({
  position: 'relative',
  padding: theme.spacing(2.25),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  [theme.breakpoints.up('md')]: {padding: theme.spacing(3)},
}));

export function ListingCard({
  title,
  href,
  image,
  aspectRatio,
  fixedMedia,
  deferImage,
  tags,
  badges,
  primaryMeta,
  meta,
  hoverActions,
  onClick,
}: ListingCardProps) {
  return (
    <CardRoot>
      <ListingCardMedia
        image={image}
        aspectRatio={aspectRatio}
        fixedMedia={fixedMedia}
        deferImage={deferImage}
        tags={tags}
        hoverActions={hoverActions}
      />
      <Body>
        {badges && <ListingCardBadges badges={badges} />}
        <Typography
          size="body-1"
          weight="semiBold"
          title={title}
          sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.75}}
        >
          {title}
        </Typography>
        {primaryMeta && <ListingCardPrimaryMeta item={primaryMeta} />}
        {meta && <ListingCardMeta items={meta} />}
      </Body>
      {href && (
        <CardLink href={href} onClick={onClick} aria-label={title}>
          <CardPendingOverlay />
        </CardLink>
      )}
    </CardRoot>
  );
}

ListingCard.displayName = 'ListingCard';
